import { MathUtil } from "@base/math/MathUtil";
import { Vec2 } from "@base/math/Vec2";
import { IVec3, Vec3 } from "@base/math/Vec3";
import { gtc, ltc } from "@base/util/Comma";
import { mapToArray } from "@base/util/TypeUtil";
import { RopeHelper } from "@biz/articles/RopeHelper";
import { OrderType } from "@biz/Definitions";
import { AdditionalArticleInfos, AdditionalArticleInfosOption, getAvailability, IArticle } from "@biz/definitions/ArticleDefinitions";
import { ArticleFeatureStore } from "@biz/definitions/ArticleFeatureDefinitions";
import { appTemplates } from "@biz/definitions/ArticleShopTemplates";
import { FL } from "@biz/definitions/FL";
import { kederInfos } from "@biz/definitions/KederDefinitions";
import { backenklemmeLoseArticleId, RemoteChannelCount } from "@biz/definitions/MountDefinitons";
import { Product } from "@biz/definitions/Product";
import { SST } from "@biz/definitions/SST";
import { StandardTemplate } from "@biz/definitions/StandardTemplate";
import { ClothType, EdgeMountItemType, EdgeName, IRemoteInfo, ISunSailData, PoleId, PoleIndex, SunSailType, WaterDirection } from "@biz/definitions/SunSailDefinitions";
import { getCornerTypeName, getEdgePoleIds, getOfferListForCornerArticle, getPointName, getPointNameSmall, getSideName, getSideNameSmall, getSunSailNameShort } from "@biz/definitions/SunSailDefinitions.util";
import { CalcProductLogic, OrderErrorBuilder } from "@biz/items/CalcProductLogic";
import { CalcSail2dLogic } from "@biz/items/sunSail/CalcSail2dLogic";
import { CalcSail3dLogic } from "@biz/items/sunSail/CalcSail3dLogic";
import { AxleLogic } from "@biz/items/sunSail/logic/AxleLogic";
import { LogicS } from "@biz/items/sunSail/logic/LogicS";
import { PoleLogic } from "@biz/items/sunSail/logic/PoleLogic";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { ProtectiveCoverLogic } from "@biz/items/sunSail/logic/ProtectiveCoverLogic";
import { SandyLogic } from "@biz/items/sunSail/logic/SandyLogic";
import { SunSailEdges } from "@biz/items/sunSail/logic/SunSailEdges";
import { SunSailLogic } from "@biz/items/sunSail/logic/SunSailLogic";
import { SunSailTopology } from "@biz/items/sunSail/logic/SunSailTopology";
import { SunSailReducers } from "@biz/items/sunSail/SunSailReducers";
import { LanguageId } from "@biz/language/Languages";
import { ILocaContext, LocaContext, LocaFn } from "@biz/language/LocaContext";
import { AdditionalArticleInfo } from "@biz/logic/AdditionalArticleInfo";
import { ArticleLogic } from "@biz/logic/ArticleLogic";
import { ClothData, IClothData } from "@biz/logic/ClothData";
import { ClothDataLogic } from "@biz/logic/ClothDataLogic";
import { Measure } from "@biz/logic/Measure";
import { MotorLogic } from "@biz/logic/MotorLogic";
import { isPointInPolygon } from "@biz/logic/PointInPoly";
import { ISail2dForm } from "@biz/models/Sail2dDefinitions";
import { WishList } from "@biz/models/ShoppingList";
import { IBeosOrderProperties, IShoppingArticleOptions, IShoppingListContext, PartListPriority, QuickOverlayPriority } from "@biz/models/ShoppingListDefinitions";
import { BeosVarianten } from "@biz/orders/BeosVarianten";
import { ICalcContext, ICalculatedProductResult, IProjectCheckError, ProjectCheckErrorType } from "@biz/projectCalculation/ProjectDefinitions";
import { IColorItem, SunSailColorProvider } from "@biz/SunSailColors";

export class CalcSailLogic {
  static checkSandy(slc: ICalcContext, ssd: ISunSailData) {
    if (SST.is(ssd.sunSailType, SST.Sandy)) {
      const sunSailArea = SandyLogic.calcSandyClothArea(ssd);
      const { sailPoints } = SandyLogic.calcBottomSandyPositions(ssd); // https://plaspack.atlassian.net/browse/APPSK-195

      for (let i = 0; i < sailPoints.length; i++) {
        const fromIndex = i;
        const toIndex = (i + 1) % sailPoints.length;
        const sideLen = Vec3.len(sailPoints[fromIndex], sailPoints[toIndex]);

        const targetArea = 20;
        if (sunSailArea <= targetArea) { // https://plaspack.atlassian.net/browse/APP-1508
          const targetLen = 5;
          if (sideLen > targetLen)
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getSideName(fromIndex, toIndex), slc.lc.f("Rule_Sandy_PoleDistSmall", { targetArea, sunSailArea: sunSailArea.toFixed(2), targetLen, sideLen: sideLen.toFixed(2) }));
        } else {
          const targetLen = 4;
          if (sideLen > targetLen)
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getSideName(fromIndex, toIndex), slc.lc.f("Rule_Sandy_PoleDistLarge", { targetArea, sunSailArea: sunSailArea.toFixed(2), targetLen, sideLen: sideLen.toFixed(2) }));
        }
      }
    }
  }

  static addSandySailArticles(slc: IShoppingListContext): void {
    const options = CalcSailLogic.getMiniShoppingListArticleOptions("S");
    const optionsS: Partial<IShoppingArticleOptions> = { ...options, offerList: "S" }
    CalcProductLogic.addRuleArticle(slc, "60002A005", optionsS);
    CalcProductLogic.addRuleArticle(slc, "60002A006", optionsS);
    CalcProductLogic.addRuleArticle(slc, "60003A001", optionsS);
    CalcProductLogic.addRuleArticle(slc, "60003A002", optionsS);
    CalcProductLogic.addRuleArticle(slc, "00E13000522", optionsS);
    CalcProductLogic.addRuleArticle(slc, "60003A003", optionsS);

    CalcProductLogic.addRopeArticles(["6.5"], slc, options);

    CalcProductLogic.addRuleArticle(slc, "00E13001738", optionsS); // https://plaspack.atlassian.net/browse/APP-1599
    CalcProductLogic.addRuleArticle(slc, "00E13000719", optionsS); // https://plaspack.atlassian.net/browse/APP-1599
  }

  static getWaterDirections(sunSailType: SunSailType, poleCount: number, lc: ILocaContext): { value: WaterDirection; label: string | React.ReactNode }[] {
    if (!SST.is(sunSailType, SST.SF))
      return [];
    if (poleCount === 3) {
      return [
        { value: "-", label: lc.C("Sail_WaterDirection_None") },
        { value: "A", label: lc.C("Sail_WaterDirection_Point", "A") },
        { value: "B", label: lc.C("Sail_WaterDirection_Point", "B") },
        { value: "C", label: lc.C("Sail_WaterDirection_Point", "C") },
        { value: "AB", label: lc.C("Sail_WaterDirection_Side", ["A", "B"]) },
        { value: "BC", label: lc.C("Sail_WaterDirection_Side", ["B", "C"]) },
        { value: "AC", label: lc.C("Sail_WaterDirection_Side", ["A", "C"]) },
      ];
    } else {
      return [
        { value: "-", label: lc.C("Sail_WaterDirection_None") },
        { value: "A", label: lc.C("Sail_WaterDirection_Point", "A") },
        { value: "B", label: lc.C("Sail_WaterDirection_Point", "B") },
        { value: "C", label: lc.C("Sail_WaterDirection_Point", "C") },
        { value: "D", label: lc.C("Sail_WaterDirection_Point", "D") },
        { value: "AB", label: lc.C("Sail_WaterDirection_Side", ["A", "B"]) },
        { value: "BC", label: lc.C("Sail_WaterDirection_Side", ["B", "C"]) },
        { value: "CD", label: lc.C("Sail_WaterDirection_Side", ["C", "D"]) },
        { value: "AD", label: lc.C("Sail_WaterDirection_Side", ["A", "D"]) },
      ];
    }
  }

  static getSSDArray(sunSails: Record<string, ICalculatedProductResult>): { array: ISunSailData[]; errorCount: number } {
    const orig = mapToArray(sunSails);
    const array = orig.map((r) => r.product).filter((s) => !!s)
    return {
      array: array as ISunSailData[],
      errorCount: orig.length - array.length
    }
  }

  static fillCloth(ssd: ISunSailData, cloth: IClothData | undefined, colorId: string | undefined, articleMap: Record<string, IArticle>): ISunSailData {
    if (cloth) {
      const baseArticleId = ClothData.getBaseArticleId(cloth);
      const article = articleMap[baseArticleId];
      if (article) {
        const items = article.colorGroup?.items;
        if (items) {
          const color = items.find((item) => item.variantId === colorId);
          if (color) {
            ssd = SunSailReducers.setClothAndColor(ssd, cloth, color.variantId);
          }
        }
      }
      ssd = SunSailReducers.setClothAndColor(ssd, cloth, ssd.colorId);
    }
    return ssd;
  }

  // static getColorName(colorId: string | undefined): string {
  //   if (!colorId)
  //     return "";
  //   const colorItem: IColorItem | undefined = SunSailColorProvider.getColorById(colorId);

  //   const colorName = colorItem?.displayName || colorId;
  //   return colorName;
  // }

  static calcSidesText(mountPositions: IVec3[], sides: string[] | { fromIndex: number, toIndex: number }[]): string {
    const mm = (len: number): string => (len * 1000).toFixed();
    const values: string[] = [];
    for (const side of sides) {
      let fromIndex: number;
      let toIndex: number;
      if (typeof side === "string") {
        fromIndex = side.charCodeAt(0) - 65;
        toIndex = side.charCodeAt(1) - 65;
      } else {
        fromIndex = side.fromIndex;
        toIndex = side.toIndex;
      }

      values.push(getSideName(fromIndex, toIndex) + " " + mm(Vec3.len(mountPositions[fromIndex], mountPositions[toIndex])));
    }
    return values.join(", ") + " mm";
  }

  static calcAdditionalArticleInfoForSailCSXSTwin(sunSail: ISunSailData, iGroup: number, lc: ILocaContext): AdditionalArticleInfos {
    const mountPositions = SunSailLogic.calcMountPositions(sunSail, iGroup, undefined);
    const sailMountPositions = SunSailLogic.calcSailMountPositionsFromMountPositions(sunSail, mountPositions);
    const netPositions = SunSailLogic.calcNetPositionGroups(sunSail)

    const topology = SunSailTopology.calcTopology(sunSail.sunSailType, Poles.getCount(sunSail)).groups[iGroup];

    const additionalArticleInfo: AdditionalArticleInfos = {
      axleLength: { header: lc.t("Quote_Info_AxleLength"), text: Measure.toMM(SunSailLogic.calcAxleLength(sunSail, "logical", iGroup)) }, // fertige Breite?
      sailWidth: { header: lc.t("Quote_Info_SailWidth"), text: Measure.toMM(Vec3.len(netPositions[iGroup][PoleIndex.A], netPositions[iGroup][PoleIndex.B])) }, // fertuge Känge?
      distOut: { header: lc.t("ArticleInfo_DistOut"), text: Measure.toMM(SunSailLogic.calcDistanceOut(sunSail, iGroup === 0 ? PoleIndex.C : PoleIndex.E)) },
      area: { header: lc.t("ArticleInfo_ClothArea"), text: SunSailLogic.calcSunSailAreaForGroup(sunSail, iGroup).toFixed(2) + Measure.m2 }
    }

    const hasCSMountPylons = sunSail && SunSailLogic.hasCSMountPylons(sunSail);
    const hasDiagonals = topology.poleIndices.length === 4 && hasCSMountPylons;
    additionalArticleInfo.sailMountInfo = { header: lc.t("ArticleInfo_SailMountMeasures"), text: CalcSailLogic.calcSidesText(sailMountPositions, topology.poleIndices.map((a) => ({ fromIndex: a, toIndex: (a + 1) % topology.poleIndices.length }))) }
    if (hasDiagonals) {
      additionalArticleInfo.diagonal = { header: lc.t("ArticleInfo_SailDiagonalMeasures"), text: CalcSailLogic.calcSidesText(sailMountPositions, [{ fromIndex: PoleIndex.A, toIndex: topology.nonAxleIndices[0] }, { fromIndex: PoleIndex.B, toIndex: topology.nonAxleIndices[1] }]) }
    }

    return additionalArticleInfo;
  }

  static calcAdditionalArticleInfoForSystemCSXSTwin(sunSail: ISunSailData, lc: ILocaContext): AdditionalArticleInfosOption {

    const additionalArticleInfo: AdditionalArticleInfosOption = {}

    const topology = SunSailTopology.calcTopology(sunSail.sunSailType, Poles.getCount(sunSail));
    const axleLength: string[] = [];
    const mounts: string[] = [];
    const diagonals: string[] = [];
    for (let gi = 0; gi < topology.groups.length; gi++) {
      const mountPositions = SunSailLogic.calcMountPositions(sunSail, gi, undefined);
      const group = topology.groups[gi];

      axleLength.push(Measure.toMM(SunSailLogic.calcAxleLength(sunSail, "logical", gi)));

      const hasCSMountPylons = sunSail && SunSailLogic.hasCSMountPylons(sunSail);
      if (hasCSMountPylons)
        mounts.push(CalcSailLogic.calcSidesText(mountPositions, group.poleIndices.map((a) => ({ fromIndex: a, toIndex: (a + 1) % group.poleIndices.length }))))
      const hasDiagonals = group.poleIndices.length === 4 && hasCSMountPylons;
      if (hasDiagonals) {
        diagonals.push(CalcSailLogic.calcSidesText(mountPositions, [{ fromIndex: PoleIndex.A, toIndex: group.nonAxleIndices[0] }, { fromIndex: PoleIndex.B, toIndex: group.nonAxleIndices[1] }]))
      }
    }

    additionalArticleInfo.axleLength = { header: lc.t("Quote_Info_AxleLength"), text: axleLength };
    if (mounts.length > 0) {
      additionalArticleInfo.mountInfo = { header: lc.t("ArticleInfo_MountMeasures"), text: mounts.map((m, i) => `(${i + 1}): ${m}`) }
    }
    if (diagonals.length > 0)
      additionalArticleInfo.diagonals = { header: lc.t("ArticleInfo_DiagonalMeasures"), text: diagonals }

    return additionalArticleInfo;
  }

  static calcDistInText(ssd: ISunSailData): string {
    const mm = (len: number): string => (len * 1000).toFixed();
    const values: string[] = [];
    for (let i = 0; i < Poles.getCount(ssd); i++) {
      values.push("Y" + getPointName(i) + " " + mm(-SunSailLogic.calcDistanceIn(ssd, i)));
    }
    return values.join(", ") + " mm";
  }

  static calcCornersText(ssd: ISunSailData, lc: ILocaContext, options?: { poleFilter: PoleIndex[] }): string {
    const corners: string[] = [];
    for (let p = 0; p < Poles.getCount(ssd); p++) {
      if (options?.poleFilter && !options.poleFilter.includes(p))
        continue;
      const ct = SunSailLogic.getCornerType(ssd, p);
      corners.push(getPointName(p) + " " + getCornerTypeName(ct, lc))
    }
    return corners.join(", ");
  }


  static calcEdgeItemsText(ssd: ISunSailData, sides: string[], lc: ILocaContext): string {
    if (ssd && ssd.sunSailType === SunSailType.Fixed) {
      const sideInfos: string[] = [];
      const sideNames: string[] = [];
      for (const side of sides) {
        const edgeName = side.toLowerCase() as EdgeName;
        sideNames.push(edgeName.toUpperCase());

        let sideInfo = "";

        const edgeMountItem = SunSailLogic.calcEdgeMountItemReal(ssd, edgeName);
        if (edgeMountItem) {
          // sideInfo = lc.f(edgeMountItem.count === 1 ? "EdgePositioning_OneInTheMiddle" : "EdgePositioning_CountDistance", {
          //   name: edgeMountItem.count === 1 ? lc.t(LocaContext.connect("SC_EdgeMounItem", edgeMountItem.itemType)) : lc.t(LocaContext.connect("SC_EdgeMounItem", edgeMountItem.itemType, "Multiple")),
          //   count: edgeMountItem.count,
          //   distance: MathUtil.round(edgeMountItem.dist * 100, 0)
          // })
          sideInfo = `${edgeMountItem.count}x${lc.t(LocaContext.connect("SC_EdgeMounItem", edgeMountItem.itemType))}`;
        }
        sideInfos.push(sideInfo)
      }


      if (sideInfos.length > 0) {
        const allSame = sideInfos.every((val, _i, arr) => val === arr[0])
        if (allSame && sideInfos.length > 0 && sideInfos[0])
          return `${sideInfos[0]}`;
        else {
          const infos = sideInfos.map((si, i) => si ? `${sideNames[i]} ${si}` : "").filter((item) => !!item)
          return `${infos.join(", ")}`;
        }
      }
    }
    return "";
  }

  static calcConcavitiyText(ssd: ISunSailData, sides: string[], lc: ILocaContext): string {
    if (ssd && ssd.sunSailType === SunSailType.Fixed) {
      const sideInfos: string[] = [];
      const sideNames: string[] = [];
      for (const side of sides) {
        const edgeName = side.toLowerCase() as EdgeName;
        sideNames.push(edgeName.toUpperCase());

        const concavity = SunSailLogic.getConcavity(ssd, edgeName);
        let sideInfo = "";
        if (SunSailLogic.hasKeder(ssd, edgeName)) {
          const kederSize = SunSailLogic.getKederSize(ssd, edgeName);
          sideInfo = `${lc.t("Sail_Edge_Keder")} ${kederSize}mm`
        } else if (concavity) {
          sideInfo = `${lc.t(LocaContext.connect("Sail_Concavity", concavity))}`
        }

        if (SunSailLogic.hasHemstitch(ssd, edgeName))
          sideInfo = `${sideInfo}/${lc.t("Sail_Hemstitch")}`
        sideInfos.push(sideInfo)
      }


      if (sideInfos.length > 0) {
        const allSame = sideInfos.every((val, _i, arr) => val === arr[0])
        if (allSame && sideInfos.length > 0)
          return `${sideInfos[0]}`;
        else {
          const infos = sideInfos.map((si, i) => `${sideNames[i]} ${si}`)
          return `${infos.join(", ")}`;
        }
      }
    }
    return "";
  }

  static calcAdditionalArticleInfoForSailF(sunSail: ISunSailData, lc: ILocaContext): AdditionalArticleInfos {
    const mountPositions = SunSailLogic.calcMountPositions(sunSail, 0, undefined);
    const sailMountPositions = SunSailLogic.calcSailMountPositionsFromMountPositions(sunSail, mountPositions);

    const poleCount = Poles.getCount(sunSail);
    const sides: string[] = [["AB", "BC", "AC"], ["AB", "BC", "CD", "AD"], ["AB", "BC", "CD", "DE", "AE"], ["AB", "BC", "CD", "DE", "EF", "AF"]][poleCount - 3];
    // const diagonal = ["", CalcSailLogic.calcSidesText(mountPositions, ["AC"]), CalcSailLogic.calcSidesText(mountPositions, ["AC", "AD"]), CalcSailLogic.calcSidesText(mountPositions, ["AC", "AD", "AE"])][poleCount - 3]
    const sailDiagonal = ["", CalcSailLogic.calcSidesText(sailMountPositions, ["AC"]), CalcSailLogic.calcSidesText(sailMountPositions, ["AC", "AD"]), CalcSailLogic.calcSidesText(sailMountPositions, ["AC", "AD", "AE"])][poleCount - 3]
    const additionalArticleInfo: AdditionalArticleInfos = {
      // mountInfo: { header: lc.t("ArticleInfo_MountMeasures"), text: CalcSailLogic.calcSidesText(mountPositions, sides) },
      // diagonal: { header: lc.t("ArticleInfo_DiagonalMeasures"), text: diagonal },
      sailMountInfo: { header: lc.t("ArticleInfo_SailMountMeasures"), text: CalcSailLogic.calcSidesText(sailMountPositions, sides) },
      sailDiagonal: { header: lc.t("ArticleInfo_SailDiagonalMeasures"), text: sailDiagonal },
      distIn: { header: lc.t("ArticleInfo_DistIn"), text: CalcSailLogic.calcDistInText(sunSail) },
      conncavities: { header: lc.t("ArticleInfo_Concavities"), text: CalcSailLogic.calcConcavitiyText(sunSail, sides, lc) },
      edges: { header: lc.t("ArticleInfo_Edges"), text: CalcSailLogic.calcEdgeItemsText(sunSail, sides, lc) },
      corners: { header: lc.t("ArticleInfo_Corners"), text: CalcSailLogic.calcCornersText(sunSail, lc) },
      waterDirection: { header: lc.t("ArticleInfo_WaterDirection"), text: sunSail.waterDirections?.[0] || "-" },
      area: { header: lc.t("ArticleInfo_ClothArea"), text: SunSailLogic.calcSunSailAreaCloth(sunSail).toFixed(2) + Measure.m2 }

    }
    return additionalArticleInfo;
  }

  static calcAdditionalArticleInfoForSailAMCXOneFlex(sunSail: ISunSailData, lc: ILocaContext): AdditionalArticleInfos {
    const sunSailType = sunSail.sunSailType;
    const poleCount = Poles.getCount(sunSail);
    const coilUpPole = SunSailLogic.getSailCoilUpPoleIndex(sunSail);
    const sides = poleCount === 4 ? ["AB", "BC", "CD", "AD"] : ["AB", "BC", "AC"];
    // return [PosExtraText.calcAlignDistOut(ssd)].filter((f) => !!f).join("\n");
    const pylonCount = SunSailLogic.calcPylonCount(sunSail)
    const mountPositions = SunSailLogic.calcMountPositions(sunSail, 0, undefined);
    const sailMountPositions = SunSailLogic.calcSailMountPositionsFromMountPositions(sunSail, mountPositions);
    const additionalArticleInfo: AdditionalArticleInfos = {}
    additionalArticleInfo.sailMountInfo = { header: lc.t("ArticleInfo_SailMountMeasures"), text: CalcSailLogic.calcSidesText(sailMountPositions, sides) };
    if (poleCount === 4)
      additionalArticleInfo.sailDiagonal = { header: lc.t("ArticleInfo_SailDiagonalMeasures"), text: CalcSailLogic.calcSidesText(sailMountPositions, ["AC"]) }

    if (pylonCount > 0)
      additionalArticleInfo.pylonCountText = { header: lc.t("ArticleInfo_PylonCount"), text: "" + pylonCount }
    if (sunSailType === SunSailType.SolidayX && coilUpPole !== -1)
      additionalArticleInfo.pylonCountText = { header: lc.t("ArticleInfo_CoilUpPoint"), text: getPointName(coilUpPole) }

    additionalArticleInfo.alignDistOut = { header: lc.t("ArticleInfo_AlignDistOut"), text: SunSailLogic.calcAlignDistOut(sunSail) ? lc.t("Sail_Yes") : lc.t("Sail_No") }
    if (SunSailLogic.isHighestPointRequired(sunSail.sunSailType, sunSail.poles.length))
      additionalArticleInfo.heighestPoint = { header: lc.t("ArticleInfo_HeighestPoint"), text: SunSailLogic.getHighestPointName(sunSail) || "?" }
    if (sunSail.sunSailType === SunSailType.SolidayOne) {
      additionalArticleInfo.corners = { header: lc.t("ArticleInfo_Corners"), text: CalcSailLogic.calcCornersText(sunSail, lc, { poleFilter: [PoleIndex.B, PoleIndex.D] }) }
    }

    additionalArticleInfo.area = { header: lc.t("ArticleInfo_ClothArea"), text: SunSailLogic.calcSunSailAreaCloth(sunSail).toFixed(2) + Measure.m2 }
    return additionalArticleInfo;
  }

  static calcAdditionalArticleInfoForSystemAMCXOneFlex(sunSail: ISunSailData, lc: ILocaContext): AdditionalArticleInfosOption {
    const poleCount = Poles.getCount(sunSail);
    const sides = poleCount === 4 ? ["AB", "BC", "CD", "AD"] : ["AB", "BC", "AC"];
    const mountPositions = SunSailLogic.calcMountPositions(sunSail, 0, undefined);
    const additionalArticleInfo: AdditionalArticleInfosOption = {}
    additionalArticleInfo.monutInfo = { header: lc.t("ArticleInfo_MountMeasures"), text: CalcSailLogic.calcSidesText(mountPositions, sides) }

    if (poleCount === 4)
      additionalArticleInfo.diagonal = { header: lc.t("ArticleInfo_DiagonalMeasures"), text: CalcSailLogic.calcSidesText(mountPositions, ["AC"]) }
    return additionalArticleInfo;
  }

  static calcAdditionalArticleInfoForSail(sunSail: ISunSailData, groupIndex: number, lc: ILocaContext): AdditionalArticleInfosOption {
    if (SST.is(sunSail.sunSailType, SST.CsXs, SST.Twin)) {
      return CalcSailLogic.calcAdditionalArticleInfoForSailCSXSTwin(sunSail, groupIndex, lc);
    }

    if (SST.is(sunSail.sunSailType, SST.F)) {
      return CalcSailLogic.calcAdditionalArticleInfoForSailF(sunSail, lc);
    }

    if (SST.is(sunSail.sunSailType, SST.AMCX, SST.One, SST.Flex)) {
      return CalcSailLogic.calcAdditionalArticleInfoForSailAMCXOneFlex(sunSail, lc);
    }
    if (SST.is(sunSail.sunSailType, SST.Sandy)) {
      const ret: AdditionalArticleInfosOption = {
        area: { header: lc.t("ArticleInfo_ClothSourceArea"), text: SandyLogic.calcSandyObbArea(sunSail)?.area?.toFixed(2) + Measure.m2 },
        areaCloth: { header: lc.t("ArticleInfo_ClothArea"), text: SandyLogic.calcSandyClothArea(sunSail)?.toFixed(2) + Measure.m2 },
        poleCount: { header: lc.t("ArticleInfo_PoleCount"), text: Poles.getCount(sunSail, "corner").toString() },
        patches: sunSail.patches?.length && { header: lc.t("ArticleInfo_Patches"), text: sunSail.patches.map((p) => Poles.getSideText(sunSail, p.poleIds[0], p.poleIds[1])).join(", ") },
        centerPoles: sunSail.centerPoles?.length && { header: lc.t("ArticleInfo_CenterPoles"), text: "" + sunSail.centerPoles.length }
      }
      return ret;
    }
    return {};
  }

  static calcAdditionalArticleInfoForSystem(sunSail: ISunSailData, lc: ILocaContext): AdditionalArticleInfosOption {
    if (SST.is(sunSail.sunSailType, SST.CsXs, SST.Twin)) {
      return CalcSailLogic.calcAdditionalArticleInfoForSystemCSXSTwin(sunSail, lc);
    }

    if (SST.is(sunSail.sunSailType, SST.AMCX, SST.One, SST.Flex)) {
      return CalcSailLogic.calcAdditionalArticleInfoForSystemAMCXOneFlex(sunSail, lc);
    }
    return {};
  }

  static #defaultMaxSunSailAreaTextIds: Record<ProjectCheckErrorType, LanguageId> = {
    [ProjectCheckErrorType.None]: "Rule_MaxSailArea_Warning",
    [ProjectCheckErrorType.Hint]: "Rule_MaxSailArea_Warning",
    [ProjectCheckErrorType.Warning]: "Rule_MaxSailArea_Warning",
    [ProjectCheckErrorType.Error]: "Rule_MaxSailArea",
    [ProjectCheckErrorType.FatalError]: "Rule_MaxSailArea"
  }
  public static checkMaxSunSailArea(sunSail: ISunSailData, slc: ICalcContext): void {
    const sunSailType = sunSail.sunSailType;
    const poleCount = sunSail.poles.length;
    const sunSailArea = SunSailLogic.calcSunSailAreaCloth(sunSail);
    const pylonCount = SunSailLogic.calcPylonCount(sunSail);
    const maxSunSailAreaItems = SunSailLogic.getMaxSunSailAreaItems(sunSailType, poleCount, sunSail.version, FL.fromProduct(sunSail), SunSailLogic.getCloth(sunSail)?.clothType, pylonCount).sort((a, b) => b.type - a.type);
    for (const item of maxSunSailAreaItems) {
      if (gtc(sunSailArea, item.value, 2)) {
        const textId = item.textId ? item.textId : CalcSailLogic.#defaultMaxSunSailAreaTextIds[item.type];
        CalcProductLogic.addError(slc.errors, item.type, "net", slc.lc.t(textId), { params: [item.value, sunSailArea.toFixed(2)] });
        break;
      }
    }
  }

  static addSailClothArticles(slc: ICalcContext, sunSail: ISunSailData): void {

    const { sunSailType } = sunSail;

    if (!sunSail.cloth)
      return;

    const poleCount = Poles.getCount(sunSail);
    const cloth = (sunSail.cloth || ClothDataLogic.getDefaultClothData({ pdt: "sunSail", sunSailType, poleCount, version: sunSail.version }, FL.fromProduct(sunSail)))!;
    const clothCount = sunSail.clothCount || 1;

    const sailGroupCount = sunSailType === SunSailType.SolidayTwin ? 2 : 1;
    for (let iSailGroup = 0; iSailGroup < sailGroupCount; iSailGroup++) {
      const additionalArticleInfo = CalcSailLogic.calcAdditionalArticleInfoForSail(sunSail, iSailGroup, slc.lc);

      const beosVariants: IBeosOrderProperties["varianten"] = {};
      if (SST.is(sunSailType, SST.Xs)) {
        beosVariants["6"] = SunSailLogic.calcVariant06(Poles.getCount(sunSail));
      }
      if (sunSailType === SunSailType.SolidayC && poleCount === 4) {
        const highestPoint = SunSailLogic.calcHighestNonAxlePoint(sunSail);
        if (highestPoint !== undefined) {
          beosVariants["72"] = getPointName(highestPoint) + "=HOCH"// höherer Auszungspunkt B, D
        }
      }

      const standardSize = SunSailLogic.calcStandardSize(sunSail);
      const sunSailArea = sailGroupCount === 2 ? SunSailLogic.calcSunSailAreaForGroup(sunSail, iSailGroup) : SunSailLogic.calcSunSailAreaToPay(sunSail);
      const aai: AdditionalArticleInfosOption = {};
      const edgeMountItemTypes = SunSailLogic.calcEdgeMountItemTypes(sunSail);
      if (edgeMountItemTypes && Object.keys(edgeMountItemTypes).length) {
        aai.edgeMountItems = AdditionalArticleInfo.edgeMountItems(slc, edgeMountItemTypes);
      }

      const waterDirection = sunSail.waterDirections?.[iSailGroup];

      CalcSailLogic.addClothArticle(slc, {
        cloth,
        count: clothCount,
        colorId: sunSail.colorId,
        sunSailArea,
        groupIndex: iSailGroup,
        additionalArticleInfo: {
          colorId: AdditionalArticleInfo.colorId(slc, sunSail.colorId || ""),
          area: AdditionalArticleInfo.areaM2(slc, sunSailArea),
          ...aai,
          ...additionalArticleInfo
        },
        beosOrder: { varianten: beosVariants },
        features: {
          colorId: sunSail.colorId,
          standardTemplateId: standardSize,
          waterDirection,
          standardSize
        }
      })
    }

    CalcSailLogic.addProtectionCoverForSunSail(slc, sunSail);
    CalcSailLogic.addCornerProtectionArticles(slc, sunSail);
    if (!SST.isRaff(sunSail.sunSailType)) {
      const totalSunSailArea = SunSailLogic.calcSunSailAreaCloth(sunSail);
      CalcSail3dLogic.addSunSailBagArticles(slc, sunSail.sunSailType === SunSailType.SolidayTwin ? [totalSunSailArea, ...SunSailLogic.calcSunSailGroupAreas(sunSail)] : [totalSunSailArea], sunSail.sunSailType === SunSailType.SolidayFlex ? 1 : 0);
    }

    if (SST.is(sunSailType, SST.CoSF)) {
      CalcSailLogic.calcCSFArticles(slc, sunSail.sunSailType, sunSail.orderType);
    }

    if (SST.is(sunSailType, SST.Sandy)) {
      CalcSailLogic.addSandySailArticles(slc);
    }

    if (SST.is(sunSailType, SST.CsTwin, SunSailType.SolidayC)) {
      CalcProductLogic.addRuleArticle(slc, "00E13002086", { count: 0, articleType: "optional" }); // Pylonseilgriff neu
      CalcProductLogic.addRuleArticle(slc, "00E13000262", { count: 0, articleType: "optional" }); // Pylonseilgriff alt
    }

    if (SST.is(sunSailType, SST.Flex)) {
      const flexClamps = SunSailLogic.calcFlexClampCount(sunSail);
      CalcProductLogic.addRuleArticle(slc, "00E13002084", { count: flexClamps, offerList: "KS", partList: { list: "sunSailList" } });
    }
  }

  static getMiniShoppingListArticleOptions(orderType: OrderType | undefined): Partial<IShoppingArticleOptions> {
    return { count: 0, articleType: "optional", partList: { list: orderType === "S" ? "sunSailList" : "stockList", group: "Warenkorb" }, idCategory: "miniCart", articleGroup: "miniCart", quickCategory: "miniCart" };
  }

  static calcCSFArticles(slc: IShoppingListContext, sunSailType: SunSailType, orderType: OrderType | undefined): void {
    const options = CalcSailLogic.getMiniShoppingListArticleOptions(orderType);

    if (sunSailType === SunSailType.Standard) {
      CalcProductLogic.addRuleArticle(slc, "00E13000827", options); // Gewindespanner M8 aus Edelstahl Gabel/Gabel
      CalcProductLogic.addRuleArticle(slc, "00E13000828", options); // Gewindespanner M8 aus Edelstahl Gabel/Gabel
      CalcProductLogic.addRuleArticle(slc, "00E13000829", options); // Gewindespanner M8 aus Edelstahl Gabel/Gabel
    }

    // 00E13000827 bag
    CalcProductLogic.addRuleArticle(slc, "00E13000126", options); // Gewindespanner M8 aus Edelstahl Gabel/Gabel
    CalcProductLogic.addRuleArticle(slc, "00E13000130", options); // Gewindespanner M10 aus Edelstahl Gabel/Gabel
    CalcProductLogic.addRuleArticle(slc, "00E13000801", options); // Spanngurt mit Schnappschäkel
    CalcProductLogic.addRuleArticle(slc, "00E13000129", options); // Gurtbandratsche aus Edelstahl f. Verspannung
    CalcProductLogic.addRuleArticle(slc, "00E13000024", options); // Profi-Seilspanner schwarz
    CalcProductLogic.addRuleArticle(slc, "00E13000026", options); // Schaekel 5 cm mit Schnellverschluss
    CalcProductLogic.addRuleArticle(slc, "00E13000417", options); // Schnappschaekel 52mm aus Edelstahl
    CalcProductLogic.addRuleArticle(slc, "60002A008", options); // Karabinerhaken 50mm aus Edelstahl
    CalcProductLogic.addRuleArticle(slc, "60002A006", options); // Karabinerhaken 70mm aus Edelstahl
    CalcProductLogic.addRuleArticle(slc, "00E13000066", options); // Ringbolzen M8x80 L=80mm f. Edelstahlmast
    CalcProductLogic.addRuleArticle(slc, "00E13000127", options); // Ringbolzen M8x100 L=100 mm f. Alumast
    CalcProductLogic.addRuleArticle(slc, "00E13000063", options); // Ringschraube 8 mm für Holz/Wand
    CalcProductLogic.addRuleArticle(slc, "00E13001220", options); // Seilklemme mit seitlichem Eingang
    CalcProductLogic.addRuleArticle(slc, backenklemmeLoseArticleId, options); // Soliday Backenklemme, kugelgelagert, lose
    CalcProductLogic.addRuleArticle(slc, "00E13000030", options); // Soliday Sicherheits-Seilklemme mit Autorelease-Funktion
    CalcProductLogic.addRuleArticle(slc, "00E13000042", options); // Professionelle Seilrolle 8 für Seile bis 8 mm, Einzelblock
    CalcProductLogic.addRuleArticle(slc, "00E13001482", options); // Professionelle Seilrolle 10 für Seile bis 10 mm, Einzelblock
    CalcProductLogic.addRuleArticle(slc, "00E13001151", options); // Schwerlastrolle 10 für Seile bis 10 mm, für dauerhafte Belastung
    CalcProductLogic.addRuleArticle(slc, "00E13000053", options); // Kunststoffkausche 6 mm (Krok)
    CalcProductLogic.addRuleArticle(slc, "00E13001415", options); // Kunststoffkausche 8 mm (Krok)
    CalcProductLogic.addRuleArticle(slc, "00E13000153", options); // Kunststoffkausche 10 mm (Krok)

    CalcProductLogic.addRopeArticles(["6.5", "8", "10"], slc, options);
  }

  static getCornerProtectionArticles(packageSize: number, alignDistOut: boolean): string[] {
    if (packageSize === 2) {
      if (alignDistOut)
        return ["71004A003", "71004A004"];
      else
        return ["71004A005", "71004A006"];
    } else if (packageSize === 1) {
      return ["71004A020", "71004A021"];
    }
    return [];
  }

  static addCornerProtectionArticles(slc: IShoppingListContext, ssd: ISunSailData): void {
    const clothCount = ssd.clothCount || 1;
    const clothType = ssd.cloth?.clothType;
    let packageSize = 0;
    if (ssd.sunSailType === SunSailType.SolidayOne) {
      packageSize = (SunSailLogic.getCornerArticleId(ssd, PoleIndex.B) === "00E13001416" ? 1 : 0) + (SunSailLogic.getCornerArticleId(ssd, PoleIndex.D) === "00E13001416" ? 1 : 0);
    } else if (ssd.sunSailType === SunSailType.SolidayC) {
      if (clothType === ClothType.Austrosail_Nano || clothType === ClothType.Austrosail_Nano_Protect || clothType === ClothType.Concept) {
        packageSize = 2;
      }
    } else if (ssd.sunSailType === SunSailType.SolidayCS && Poles.getCount(ssd) === 3) {
      if (clothType === ClothType.Austrosail_Nano || clothType === ClothType.Austrosail_Nano_Protect || clothType === ClothType.Concept) {
        packageSize = 1;
      }
    }

    if (packageSize > 0) {
      const cornerArticles = CalcSailLogic.getCornerProtectionArticles(packageSize, SunSailLogic.calcAlignDistOut(ssd));
      CalcProductLogic.addRuleArticle(slc, cornerArticles, { idCategory: "cornerProtection", count: clothCount, articleType: "optional", partList: { list: "sunSailList" }, offerList: "S", quickOverlayPriority: QuickOverlayPriority.cornerProtection });  // Plattenabdeckung stone Soliday Schutzhülle 4-Eck
    }
  }

  static addProtectionCoverForSunSail(slc: IShoppingListContext, ssd: ISunSailData): void {
    if (!AxleLogic.hasAxle(ssd.sunSailType))
      return;

    CalcSailLogic.addProtectiveCoverArticles(slc, ssd)
  }

  // ...protectiveCoverStandard,
  // colorId: {
  //   values: (context) => ClothDataLogic.getProtectionColors(context.articleMap),
  //   additionalArticleInfo: (val, context) => {
  //     const c = context.colorMap["" + val];
  //     const label = c?.displayName || "";
  //     return { header: context.lc.t("ArticleInfo_Color"), text: label }
  //   },
  //   formatValue: (val, context) => {
  //     return FeatureFormatters.renderColorComboItem("" + val, context)
  //   },
  //   beosVariants: (val) => {
  //     if (!val)
  //       return undefined;
  //     return {
  //       "41": "" + val
  //     }
  //   }

  static addProtectiveCoverArticles(slc: IShoppingListContext, ssd: ISunSailData): void {
    const sunSailType = ssd.sunSailType;
    const axleLength = SunSailLogic.calcAxleLength(ssd, "logical");
    const coverLength = ProtectiveCoverLogic.calcCoverLength(ssd.sunSailType, axleLength);
    const granularity = ssd.sunSailType === SunSailType.SolidayTwin ? 2 : 1;
    const clothCount = ssd.clothCount || 1;

    const initialCount = sunSailType === SunSailType.SolidayFlex ? 0 : granularity * clothCount;

    let articles: string[] = [];
    switch (sunSailType) {
      case SunSailType.SolidayA:
      case SunSailType.SolidayOne:
      case SunSailType.SolidayFlex: // todo: Soliday-Flex
      case SunSailType.SolidayM:
      case SunSailType.SolidayCS:
      case SunSailType.SolidayTwin: {
        articles = ["73004A001", "70004A007", "73004A002", "70004A008"];
        break;
      }
      case SunSailType.SolidayC:
      case SunSailType.SolidayX:
      case SunSailType.SolidayXS: {
        articles = ["71004A010", "71004A011", "71004A012", "71004A013"];
      } break;
    }


    CalcProductLogic.addRuleArticle(slc, articles, {
      idCategory: "protectiveCover",
      count: initialCount, mul: coverLength, granularity,
      offerList: "S", articleType: "optional",
      featureTemplate: appTemplates.protectiveCover,
      partList: { list: "sunSailList" },
      quickOverlayPriority: QuickOverlayPriority.protectiveCover,
      features: { sst: getSunSailNameShort(sunSailType), axleLength },
    });
  }

  static calcClothColor(clothData: IClothData, colorId: string, lc: ILocaContext): { error?: IProjectCheckError, colorItem?: IColorItem } {
    const clothType = clothData.clothType;
    if (clothType === ClothType.None)
      return { error: CalcProductLogic.createError(ProjectCheckErrorType.FatalError, "net", "Error_Enter_Missing_Cloth") }

    if (!colorId)
      return { error: CalcProductLogic.createError(ProjectCheckErrorType.FatalError, "net", "Error_Enter_Missing_ClothColor") }
    const colorItem = SunSailColorProvider.getColorById(colorId);
    if (!colorItem)
      return { error: CalcProductLogic.createError(ProjectCheckErrorType.FatalError, "net", "Error_ClothColor_NotFound") }
    else {
      const availability = getAvailability(colorItem.availability);
      if (availability === "unavailable")
        return { error: CalcProductLogic.createError(ProjectCheckErrorType.Error, "net", lc.t(LocaContext.connect("Error_ClothColor", availability))) }
      else if (availability !== "available")
        return { error: CalcProductLogic.createError(ProjectCheckErrorType.Warning, "net", lc.t(LocaContext.connect("Error_ClothColor", availability))) }
    }
    return { colorItem };
  }

  static calcBaseClothArticleOptions(sunSailArea: number, isStandard: boolean, clothCount: number, groupIndex?: number): Partial<IShoppingArticleOptions> {
    const baseArticleOptions: Partial<IShoppingArticleOptions> = {
      mul: isStandard ? 1 : sunSailArea,
      stkUnit: "Stk",
      mulUnit: isStandard ? "Stk" : "m2",
      partList: { priority: PartListPriority.sail },
      idCategory: "sailCloth",
      affiliation: { groupIndex: groupIndex !== undefined ? groupIndex : 0 },
      offerList: "S",
      count: clothCount,
    }
    return baseArticleOptions;
  }

  static addClothArticle(slc: ICalcContext, options: {
    cloth: IClothData;
    count?: number;
    colorId: string;
    sunSailArea: number;
    groupIndex?: number;
    additionalArticleInfo?: AdditionalArticleInfosOption;
    features?: ArticleFeatureStore;
    beosOrder?: IBeosOrderProperties;
  }): void {

    const { cloth, colorId, sunSailArea, groupIndex, additionalArticleInfo, features, beosOrder, count } = options;

    const clothColorRet = CalcSailLogic.calcClothColor(cloth, colorId, slc.lc);
    if (clothColorRet.error) {
      slc.errors?.push(clothColorRet.error);
    }

    const abv: BeosVarianten = {};
    const aai: AdditionalArticleInfosOption = {};
    aai.colorId = additionalArticleInfo?.colorId || (colorId ? AdditionalArticleInfo.colorId(slc, colorId?.toString() || "") : undefined);

    if (features?.standardSize) {
      abv["40"] = features.standardSize;
      aai.shape = { header: slc.lc.t("ArticleInfo_Shape"), text: slc.lc.t(LocaContext.connect("StandardShape", StandardTemplate.getTemplate(features.standardSize)!.shape)) };
      aai.size = { header: slc.lc.t("ArticleInfo_Size"), text: StandardTemplate.getSizeText(features.standardSize) + "m" };
    }

    if (features?.waterDirection)
      aai.waterDirection = { header: slc.lc.t("ArticleInfo_WaterDirection"), text: features?.waterDirection || "-" }

    abv["41"] = colorId;


    const baseArticleOptions = CalcSailLogic.calcBaseClothArticleOptions(sunSailArea, !!features?.standardSize, count ?? 1, groupIndex ?? 0);
    CalcProductLogic.addRuleArticle(slc, ClothData.getBaseArticleId(cloth), {
      ...baseArticleOptions,
      additionalArticleInfo: { ...aai, ...additionalArticleInfo },
      features,
      beosOrder: {
        ...beosOrder,
        varianten: {
          ...beosOrder?.varianten,
          ...abv
        }
      }
    });
  }

  // static addClothArticleOld(slc: ICalcContext, sunSailType: SunSailType, clothData: IClothData, colorId: string, sunSailArea: number, options: {
  //   groupIndex?: number,
  //   edgeMountItemTypes?: PartialRecord<EdgeName, EdgeMountItemType>;
  //   standard?: {
  //     standardSize: string;
  //     waterDirection?: WaterDirection;
  //   },
  //   clothCount?: number,
  //   beosVariants?: IBeosOrderProperties["varianten"] | undefined,
  //   additionalArticleInfo?: Record<string, IAdditionalArticleInfoItem>
  // }): void {
  //   const clothCount = options?.clothCount || 1;

  //   const clothColorRet = CalcSailLogic.calcClothColor(clothData, colorId, slc.lc);
  //   if (clothColorRet.error) {
  //     if (clothColorRet.error.type === ProjectCheckErrorType.FatalError) return;
  //     slc.errors?.push(clothColorRet.error);
  //   }
  //   const clothColor = clothColorRet.colorItem;

  //   const baseArticleOptions = CalcSailLogic.calcBaseClothArticleOptions(sunSailArea, clothCount, options.groupIndex);

  //   let beosVariants = options?.beosVariants;
  //   if (colorId) {
  //     beosVariants = { ...beosVariants, "41": colorId }
  //   }
  //   const baseMany: Partial<IShoppingArticleOptions> = {
  //     ...baseArticleOptions,
  //     idCategory: "sailCloth" + (options.groupIndex !== undefined ? options.groupIndex : ""),
  //     additionalArticleInfo: {
  //       color: AdditionalArticleInfo.colorId(slc, clothColor?.id || ""),
  //       ...options?.additionalArticleInfo,
  //     },
  //   }
  //   if (sunSailArea) {
  //     if (baseMany.additionalArticleInfo?.area === undefined)
  //       baseMany.additionalArticleInfo!.area = { header: slc.lc.t("ArticleInfo_Area"), text: sunSailArea.toFixed(1) + Measure.m2 };
  //   }

  //   if (beosVariants) {
  //     baseMany.beosOrder = {
  //       varianten: beosVariants
  //     }
  //   }

  //   if (options?.edgeMountItemTypes && Object.keys(options.edgeMountItemTypes).length)
  //     baseMany.additionalArticleInfo = {
  //       edgeMountItems: AdditionalArticleInfo.edgeMountItems(slc, options.edgeMountItemTypes),
  //       ...baseMany.additionalArticleInfo,
  //     }

  //   const articleOptions: Partial<IShoppingArticleOptions> = {
  //     ...baseMany,
  //     partList: { list: "sunSailList" },
  //     offerList: "S",
  //     count: clothCount,
  //   }
  //   const baseArticleId = ClothData.getBaseArticleId(clothData);

  //   if (sunSailType === SunSailType.Standard) {
  //     const standardSize = options.standard!.standardSize as StandardTemplateId;
  //     const price = RuleArticleLogic.calcPricePerItemForId(slc, baseArticleId, { standardSize, colorId });
  //     const area = StandardTemplate.getArea(standardSize);
  //     const waterDirection = options?.standard?.waterDirection;
  //     CalcProductLogic.addRuleArticleOld(slc, {
  //       articleNumber: baseArticleId,
  //       overridePrice: price,
  //       variants: {
  //         colorId,
  //         standardSize,
  //         waterDirection: waterDirection || "-"
  //       },
  //     }, {
  //       ...articleOptions,
  //       mul: 1,
  //       additionalArticleInfo: {
  //         area: { header: slc.lc.t("ArticleInfo_Area"), text: area.toFixed(1) + Measure.m2 },
  //         shape: { header: slc.lc.t("ArticleInfo_Shape"), text: slc.lc.t(LocaContext.connect("StandardShape", StandardTemplate.getTemplate(standardSize)!.shape)) },
  //         size: { header: slc.lc.t("ArticleInfo_Size"), text: StandardTemplate.getSizeText(standardSize) + "m" },
  //         waterDirection: { header: slc.lc.t("ArticleInfo_WaterDirection"), text: waterDirection || "-" },
  //         ...(articleOptions.additionalArticleInfo || {}),
  //       },
  //       mulUnit: "Stk", // für Standardsegel
  //       beosOrder: {
  //         ...articleOptions.beosOrder,
  //         quadratMeter: 1, // area nicht für Standardsegel wird in der AS400 multipliziert!
  //         varianten: {
  //           "40": options.standard!.standardSize,
  //           "41": colorId
  //         }
  //       }
  //     }); // overwrite mul => just 1 Stk
  //   } else if (SST.isRaff(sunSailType)) {
  //     throw new Error("Not supported anymore!");
  //   } else {
  //     CalcProductLogic.addRuleArticleOld(slc, {
  //       articleNumber: baseArticleId,
  //       variants: {
  //         colorId,
  //       }
  //     }, articleOptions);
  //   }
  // }

  static addRemoteMotorArticles(slc: ICalcContext, sunSailType: SunSailType, remoteChannelInfo: RemoteChannelCount): void {
    if (remoteChannelInfo.motorChannels > 0) {
      if (remoteChannelInfo.motorRemoteType === "centronic") { // https://plaspack.atlassian.net/jira/software/projects/APP22/boards/2?selectedIssue=APP22-650
        const items = MotorLogic.getSunWindSensors("centronic");
        for (const item of items) {
          const found = WishList.findShoppingListItem(slc.wishList, item.articleNumber) // https://plaspack.atlassian.net/browse/APP-1511
          if (!item.disabled || found.length) {
            const options: Partial<IShoppingArticleOptions> = { articleType: "optional", quickCategory: "motorAdditional" };
            if (found.length) {
              options.count = WishList.getArticleCount(slc.wishList, item.articleNumber, { poleIndexFilter: -1 })
            }
            CalcProductLogic.addRuleArticle(slc, item.articleNumber, options)
          }
        }
      } else if (remoteChannelInfo.motorRemoteType === "centronic+") {
        const both = [...MotorLogic.getSunWindSensors("centronic+"), ...MotorLogic.getSunWindSensors("centronic")];
        for (const b of both) {
          const found = WishList.findShoppingListItem(slc.wishList, b.articleNumber) // https://plaspack.atlassian.net/browse/APP-1511
          if (!b.disabled || found.length) {
            const options: Partial<IShoppingArticleOptions> = { articleType: "optional", quickCategory: "windSensor" };
            if (found.length) {
              options.count = WishList.getArticleCount(slc.wishList, b.articleNumber, { poleIndexFilter: -1 })
            }
            CalcProductLogic.addRuleArticle(slc, b.articleNumber, options)
          }
        }
      }

      CalcProductLogic.addRuleArticle(slc, "00E13001800", { articleType: "optional", quickCategory: "motorAdditional" })
      CalcProductLogic.addRuleArticle(slc, "00E13001801", { articleType: "optional", quickCategory: "motorAdditional" })
      CalcProductLogic.addRuleArticle(slc, "00E13001802", { articleType: "optional", quickCategory: "motorAdditional" })
      if (SST.isSail(sunSailType))
        CalcProductLogic.addRuleArticle(slc, "00E13001924", { articleType: "optional", quickCategory: "motorAdditional" })

      CalcProductLogic.addRuleArticle(slc, "00E13000880", { articleType: "optional", quickCategory: "motorAdditional" })
      CalcProductLogic.addRuleArticle(slc, "00E13002099", { articleType: "optional", quickCategory: "motorAdditional" })
      CalcProductLogic.addRuleArticle(slc, "00E13000699", { articleType: "optional", quickCategory: "motorAdditional" })

      // if (remoteChannelInfo.motorRemoteType === "centronic+" && sunSailType !== SunSailType.RaffC)
      //  CalcProductLogic.addRuleArticle(slc, "71003A029", { articleType: "optional", quickCategory: "motorAdditional" }) // https://plaspack.atlassian.net/browse/APP22-694
    }

    CalcSailLogic.checkHomeCentral(slc, remoteChannelInfo);
  }

  static checkHomeCentral(slc: ICalcContext, remoteChannelInfo: RemoteChannelCount): void {
    if (remoteChannelInfo.motorChannels > 0) {
      const centralCount = WishList.getArticleCount(slc.wishList, "00E13001800", { poleIndexFilter: -1 });
      if (centralCount > 0) {
        const senderCount = WishList.getArticleCount(slc.wishList, ["00E13001801", "00E13001802"], { poleIndexFilter: -1 });
        if (senderCount === 0)
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "product", slc.lc.t("Rule_Home_Central"))
      }
    }
  }

  static addRemoteArticles(slc: ICalcContext, sunSailType: SunSailType, remoteChannelInfo: RemoteChannelCount): void {
    // 1672
    CalcSailLogic.addRemoteMotorArticles(slc, sunSailType, remoteChannelInfo);
    const remotes = CalcSailLogic.calcAllowedRemotes(remoteChannelInfo, !SST.isRaff(sunSailType));

    // add sunWindSensorConfigurable remotes
    for (const r of remotes) {
      if (r.sunWindSensorConfigurable)
        CalcProductLogic.addRuleArticle(slc, r.articleNumber, { articleType: "optional", quickCategory: r.type === "centronic+" ? "remote+" : "remote", count: r.articleNumber === "00E13001922" ? 1 : 0 });
    }

    const sunWindSensorConfigurableRemotes = remotes.filter((r) => r.sunWindSensorConfigurable).map((r) => r.articleNumber);
    const sunWindSensorConfigurableRemoteCount = WishList.getArticleCount(slc.wishList, sunWindSensorConfigurableRemotes, { poleIndexFilter: -1 });

    const centronicSunWindSensorIds = MotorLogic.getSunWindSensors("centronic").map((m) => m.articleNumber);
    const sunWindSensorCentronicCount = WishList.getArticleCount(slc.wishList, centronicSunWindSensorIds, { poleIndexFilter: -1 });

    // if sunWindSensorConfigurable remote added => also add remotes that are not configurable
    if (sunWindSensorConfigurableRemoteCount > 0 || sunWindSensorCentronicCount === 0) {
      for (const r of remotes) {
        if (!r.sunWindSensorConfigurable)
          CalcProductLogic.addRuleArticle(slc, r.articleNumber, { articleType: "optional", quickCategory: r.type === "centronic+" ? "remote+" : "remote" });
      }
    }

    // CalcSailLogic.checkRemoteArticles(slc, remoteChannelInfo);
  }

  static remotes: Record<string, IRemoteInfo> = {
    "71003A002": { articleNumber: "71003A002", sunWindSensorConfigurable: true, channelCount: 1, type: "centronic" },
    "71003A001": { articleNumber: "71003A001", sunWindSensorConfigurable: false, channelCount: 1, type: "centronic" },
    "71003A037": { articleNumber: "71003A037", sunWindSensorConfigurable: true, channelCount: 5, type: "centronic" },
    "71003A042": { articleNumber: "71003A042", sunWindSensorConfigurable: true, channelCount: 5, type: "centronic" },
    "71003A034": { articleNumber: "71003A034", sunWindSensorConfigurable: false, channelCount: 5, type: "centronic" },
    "71003A040": { articleNumber: "71003A040", sunWindSensorConfigurable: false, channelCount: 5, type: "centronic" },
    "71002A010": { articleNumber: "71002A010", sunWindSensorConfigurable: false, channelCount: 1, type: "centronic" },
    "00E13001921": { articleNumber: "00E13001921", sunWindSensorConfigurable: true, channelCount: 1, type: "centronic+" },
    "00E13001922": { articleNumber: "00E13001922", sunWindSensorConfigurable: true, channelCount: 8, type: "centronic+" },
    "00E13001923": { articleNumber: "00E13001923", sunWindSensorConfigurable: true, channelCount: 16, type: "centronic+" }
  }

  static calcAllowedRemotes(remoteChannelInfo: RemoteChannelCount, withCentronicOld: boolean): IRemoteInfo[] {
    const remoteInfo: IRemoteInfo[] = [];
    if (withCentronicOld && remoteChannelInfo.totalChannels >= 1) {
      if (remoteChannelInfo.totalChannels === 1) {
        remoteInfo.push(CalcSailLogic.remotes["71003A002"])
        remoteInfo.push(CalcSailLogic.remotes["71003A001"])
      }
      remoteInfo.push(CalcSailLogic.remotes["71003A037"])
      remoteInfo.push(CalcSailLogic.remotes["71003A042"])
      remoteInfo.push(CalcSailLogic.remotes["71003A034"])
      remoteInfo.push(CalcSailLogic.remotes["71003A040"])
      remoteInfo.push(CalcSailLogic.remotes["71002A010"])
    }

    if (remoteChannelInfo.motorRemoteType === "centronic+") {
      remoteInfo.push(CalcSailLogic.remotes["00E13001921"])
      remoteInfo.push(CalcSailLogic.remotes["00E13001922"])
      remoteInfo.push(CalcSailLogic.remotes["00E13001923"])
    }

    return remoteInfo;
  }

  static checkPylons(sunSail: ISunSailData, errors: IProjectCheckError[], t: LocaFn): void {
    // **** pylonCount
    const pylonCount = SunSailLogic.calcPylonCount(sunSail);
    if (SST.is(sunSail.sunSailType, SST.CsTwin)) {
      for (let i = 0; i < Poles.getCount(sunSail); i++) {
        if (!PoleLogic.isAxle(sunSail.sunSailType, i) && !SunSailLogic.getPolePylonType(sunSail, i))
          CalcProductLogic.addError(errors, ProjectCheckErrorType.FatalError, "product", t("Rule_PylonRequired"), { params: [getPointName(i)] }).addFieldsDirect<ISail2dForm>("sunSail", "pylons");
      }
    } else if (sunSail.sunSailType === SST.C) {
      if (pylonCount <= 0 || pylonCount > 2)
        CalcProductLogic.addError(errors, ProjectCheckErrorType.FatalError, "product", t("Rule_PylonCountRange"), { params: [1, 2, pylonCount] }).addFieldsDirect<ISail2dForm>("sunSail", "pylons");
      if (pylonCount === 1) {
        const axlePylonCount = SunSailLogic.calcAxlePylonCount(sunSail);
        if (axlePylonCount === 1)
          CalcProductLogic.addError(errors, ProjectCheckErrorType.FatalError, "product", t("Rule_PylonOneAxle"), { params: [1, 2, pylonCount] }).addFieldsDirect<ISail2dForm>("sunSail", "pylons");
      }
    }
  }


  static checkCSPolePositions(slc: ICalcContext, ssd: ISunSailData): void {
    if (ssd && SST.is(ssd.sunSailType, SST.CsXsTwin) && Poles.getCount(ssd) >= 4) {

      const topology = SunSailTopology.calcTopology(ssd.sunSailType, Poles.getCount(ssd));
      for (let groupIndex = 0; groupIndex < topology.groups.length; groupIndex++) {
        const group = topology.groups[groupIndex];
        if (group.poleIndices.length === 4) {
          const polyPos = SunSailLogic.isPolyPositioningForGroup(ssd, groupIndex).enabled;
          if (polyPos) {
            CalcSail3dLogic.checkSymmetricCS(slc.errors, ssd, group.nonAxleIndices[0], group.nonAxleIndices[1], slc.lc);

            const positions = ssd.form ? SunSailLogic.calcMountPositions(ssd, 0, undefined) : ssd.poles.map((p) => p.position)
            const polyCheckThreshold = ssd.form ? 0.01 : 0.05;

            for (const poleIndex of group.nonAxleIndices) {
              const region = LogicS.calcAllowedRegion(ssd, poleIndex);
              const newPos2 = Vec2.fromXZ(positions[poleIndex]);
              const regionCenter = Vec2.center(region.region)
              const newPos3 = Vec2.addScaled(newPos2, Vec2.dir(newPos2, regionCenter), polyCheckThreshold);
              const inside = isPointInPolygon(region.region, newPos3) // move towards region center to avoid point calculated outside although on line...
              if (!inside) {
                CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Error, getPointName(poleIndex), slc.lc.f("Rule_CS_Pylon_Poly_Outside", { poleName: getPointName(poleIndex) }));
              }
            }

          } else {
            const dists: number[] = [];
            for (const poleIndex of group.nonAxleIndices) {
              const distDirect = CalcSail2dLogic.calcCSDistanceToFrontLine(ssd, poleIndex);
              dists.push(distDirect);
              const dist = Math.round(1000 * Math.abs(distDirect));
              const maxDist = 200;
              if (dist > maxDist) {
                CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Error, getPointName(poleIndex), slc.lc.f("Rule_CS_Pylon_Fix", { poleName: getPointName(poleIndex), dist, maxDist }));
              }
            }
            if (dists.length === 2) {
              const minDist = 0.03;
              if (Math.abs(dists[0]) > minDist && Math.abs(dists[1]) > minDist) {
                if (Math.sign(dists[0]) === Math.sign(dists[1]))
                  CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getSideNameSmall(group.nonAxleIndices[0], group.nonAxleIndices[1]), slc.lc.f("Rule_CS_Pylon_Fix_Symmetric", [getPointName(group.nonAxleIndices[0]), getPointName(group.nonAxleIndices[1])]));
              }
            }
          }
        }
      }
    }
  }

  static addRope2Articles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    const ropeLength = SunSailLogic.calcRope2Length(sunSail)
    if (ropeLength > 0) {
      const rope2 = SunSailLogic.getRope2(sunSail)
      if (rope2) {
        CalcProductLogic.addRuleArticle(slc, rope2, { count: 1, articleGroup: "system", offerList: "KS", additionalArticleInfo: { usage: () => AdditionalArticleInfo.usage(slc, "RopeUsage_Rope2") }, idCategory: "rope2", mul: ropeLength, mulUnit: "m" }); // Seil aufrollen
        const kausche = RopeHelper.getKauscheForRopeArticle(rope2);
        if (kausche)
          CalcProductLogic.addRuleArticle(slc, kausche, { count: 1, articleGroup: "system", offerList: "KS", idCategory: "rope2", partList: { list: "sunSailList" } }); // Kausche
      }
    }
  }

  static calcSailMountShackleOptions(_sunSail: ISunSailData, poleId: PoleId): Partial<IShoppingArticleOptions> | undefined {
    return { affiliation: { poleId }, count: 1, articleType: "optional", idCategory: "sailMount" };
  }

  static calcSailMountRopeKauscheOptions(_sunSail: ISunSailData, poleId: PoleId): Partial<IShoppingArticleOptions> | undefined {
    return { affiliation: { poleId }, count: 1, articleType: "optional", idCategory: "sailMount" }
  }

  static addHemstitchArticles(slc: IShoppingListContext, ssd: ISunSailData): void {
    if (SST.is(ssd.sunSailType, SST.CoSF)) {
      const edgeNames = SunSailEdges.getEdgeNames(ssd.sunSailType, Poles.getCount(ssd));
      const netPositionGroups = SunSailLogic.calcNetPositionGroups(ssd, {});
      let hemStitchLength = 0;
      for (const edgeName of edgeNames) {
        const hasHemStitch = SunSailLogic.hasHemstitch(ssd, edgeName);
        if (hasHemStitch) {
          const clothIndex = SunSailLogic.calcClothIndexFromEdge(ssd.sunSailType, Poles.getCount(ssd), edgeName);
          const edgePoles = getEdgePoleIds(edgeName);
          const len = Vec3.len(netPositionGroups[clothIndex][edgePoles[0]], netPositionGroups[clothIndex][edgePoles[1]]);
          hemStitchLength += len;
        }
      }
      // const circumference = Math.ceil(SunSailLogic.calcSunSailCircumference(ssd));
      if (hemStitchLength > 0)
        CalcProductLogic.addRuleArticle(slc, "00E13001572", { count: Math.ceil((ssd.clothCount || 1) * hemStitchLength), partList: { list: "none" }, offerList: "S" });
    }
  }

  static addKederArticles(slc: ICalcContext, ssd: ISunSailData): void {
    let kederCount = 0;
    if (SST.is(ssd.sunSailType, SST.F)) {
      const clothCount = ssd.clothCount || 1;
      const edgeNames = SunSailEdges.getEdgeNames(ssd.sunSailType, Poles.getCount(ssd));
      for (const edgeName of edgeNames) {
        const kederSize = SunSailLogic.getKederSize(ssd, edgeName);
        if (kederSize) {
          kederCount++;
          const kederInfo = kederInfos[kederSize];
          if (kederInfo) {
            CalcProductLogic.addRuleArticle(slc, kederInfo.articleNumber, { affiliation: { edgeName }, count: clothCount, mul: SunSailLogic.calcKederLength(ssd, edgeName), idCategory: "Keder", mulUnit: "m", partList: { list: "none" }, offerList: "S" });
          }
          const kederRail = SunSailLogic.getKederRail(ssd, edgeName);
          if (kederRail) {
            const railLength = SunSailLogic.calcKederRailLength(ssd, edgeName);
            const additionalArticleInfo: AdditionalArticleInfos = {
              length: AdditionalArticleInfo.lengthToMM(slc, railLength),
            }
            CalcProductLogic.addRuleArticle(slc, kederRail, { features: { length: railLength }, affiliation: { edgeName }, count: clothCount, mul: railLength, idCategory: "Keder", mulUnit: "m", partList: { list: "stockList" }, additionalArticleInfo });

            const setArticle = kederRail === "00E13000282" ? "00E13001953" : "00E13001954"
            CalcProductLogic.addRuleArticle(slc, setArticle, { affiliation: { edgeName }, count: clothCount, articleType: "optional" });
          }
        }
      }
    }

    if (kederCount > 1)
      CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, "product", slc.lc.t("Rule_Keder_TooMany"));
  }

  static checkAngle(errors: IProjectCheckError[], positions: IVec3[], pre: number, current: number, next: number, maxAngleWarning: number, maxAngleError: number | undefined, t: LocaFn): void {
    const angle = SunSailLogic.calcNetAngle(positions, pre, current, next);
    const angleDeg = MathUtil.rad2deg(angle);
    if (maxAngleError !== undefined) {
      if (gtc(angleDeg, maxAngleError, 1)) {
        CalcProductLogic.addError(errors, ProjectCheckErrorType.Error, getSideNameSmall(current, next, true), t("Rule_MaxAngle"), { params: [getPointNameSmall(pre) + getPointNameSmall(current) + getPointNameSmall(next), maxAngleError, angleDeg.toFixed(1)] });
        return;
      }
    }
    if (gtc(angleDeg, maxAngleWarning, 1))
      CalcProductLogic.addError(errors, ProjectCheckErrorType.Warning, getSideNameSmall(current, next, true), t("Rule_MaxAngle"), { params: [getPointNameSmall(pre) + getPointNameSmall(current) + getPointNameSmall(next), maxAngleWarning, angleDeg.toFixed(1)] });
  }

  static checkAngles(c: ICalcContext, sunSailType: SunSailType, poleCount: number, netPositionGroups: IVec3[][]): void {
    const maxAngleWarning = 80;
    const maxAngleError = 90;
    const lc = c.lc;
    if (SST.is(sunSailType, SST.AMCXOne)) {
      CalcSailLogic.checkAngle(c.errors, netPositionGroups[0], 2, 0, 1, maxAngleWarning, maxAngleError, lc.t);
      CalcSailLogic.checkAngle(c.errors, netPositionGroups[0], 0, 2, 1, maxAngleWarning, maxAngleError, lc.t);
      if (poleCount >= 4) {
        CalcSailLogic.checkAngle(c.errors, netPositionGroups[1], 2, 0, 3, maxAngleWarning, maxAngleError, lc.t);
        CalcSailLogic.checkAngle(c.errors, netPositionGroups[1], 0, 2, 3, maxAngleWarning, maxAngleError, lc.t);
      }
    } else if (SST.is(sunSailType, SST.CsXsTwin)) {
      if (poleCount === 3) {
        CalcSailLogic.checkAngle(c.errors, netPositionGroups[0], 0, 1, 2, maxAngleWarning, maxAngleError, lc.t);
        CalcSailLogic.checkAngle(c.errors, netPositionGroups[0], 1, 0, 2, maxAngleWarning, maxAngleError, lc.t);
      } else if (poleCount === 5) {
        CalcSailLogic.checkAngle(c.errors, netPositionGroups[1], 0, 1, 4, maxAngleWarning, maxAngleError, lc.t);
        CalcSailLogic.checkAngle(c.errors, netPositionGroups[1], 1, 0, 4, maxAngleWarning, maxAngleError, lc.t);
      }
    }

    // const sunSailType = ssd.sunSailType;
    // if (SunSailLogic.hasAxle(ssd.sunSailType) && isSST(ssd.sunSailType, "MA", "M", "C", "X")) {
    //   const netGroups = SunSailLogic.calcNetPositionGroups(ssd, {});
    //   const topology = SunSailTopology.calcTopology(ssd.sunSailType, Poles.getPoleCount(ssd));
    //   const maxAngle = SunSailLogic.getMaxAngleAxleSail();

    //   for (let ig = 0; ig < topology.groups.length; ig++) {
    //     const poleIndices = topology.groups[ig].poleIndices;
    //     for (let i = 0; i < poleIndices.length; i++) {
    //       const poleIndex = poleIndices[i];
    //       const nextIndex = (i + 1) % poleIndices.length;
    //       const nextPoleIndex = poleIndices[nextIndex];
    //       const lastIndex = (i + poleIndices.length - 1) % poleIndices.length;
    //       const lastPoleIndex = poleIndices[lastIndex];
    //       if (SunSailLogic.isAxlePole(sunSailType, poleIndex) && (SunSailLogic.isAxlePole(sunSailType, nextPoleIndex) || SunSailLogic.isAxlePole(sunSailType, lastPoleIndex))) {
    //         const angle = SunSailLogic.calcAngleForIndex(i, poleIndices, netGroups[ig])
    //         const degAngle = MathUtil.rad2deg(angle);
    //         if (!isNaN(degAngle) && gtc(degAngle, maxAngle, 0))
    //           CalcSailLogic.addError(errors, getPoleName(poleIndex).toLowerCase() as any, lc.t("Rule_MaxAngle_Axle"), [maxAngle, degAngle.toFixed(1)]);
    //       }
    //     }
    //   }
    // }
  }

  static checkSideRatio(ssd: ISunSailData, errors: IProjectCheckError[], lc: ILocaContext, inputModel?: ISail2dForm): OrderErrorBuilder | undefined {

    const axleIndices = PoleLogic.getAxleIndices(ssd.sunSailType);
    if (!axleIndices)
      return undefined;

    const pylonCount = SunSailLogic.calcPylonCount(ssd);
    const netPositionGroups = SunSailLogic.calcNetPositionGroups(ssd, {});
    let maxRatio = 2; // https://plaspack.atlassian.net/browse/APP22-1034
    let reason = "";
    if (SST.is(ssd.sunSailType, SST.C) && Poles.getCount(ssd) === 4 && pylonCount <= 1) {
      maxRatio = 1.3;
      reason = lc.t("Rule_Reason_OnePylon");
    }
    if (SST.is(ssd.sunSailType, SST.AMOne) && Poles.getCount(ssd) === 4 && !ssd.twistingSet && SunSailLogic.isTwistingSet(ssd.sunSailType, Poles.getCount(ssd))) {
      maxRatio = 1.3;
      reason = lc.t("Rule_Reason_TwistingSet");
    }
    if (ssd.sunSailType === SunSailType.SolidayFlex) {
      maxRatio = 1.5;
      reason = lc.t("Rule_Reason_Flex");

    }

    const topology = SunSailTopology.calcTopology(ssd.sunSailType, Poles.getCount(ssd));
    for (let iGroup = 0; iGroup < topology.groups.length; iGroup++) {
      const group = topology.groups[iGroup];
      if (group.poleIndices.length === 3) {
        const netGroup = netPositionGroups[iGroup];
        const p1 = axleIndices.first;
        const p2 = axleIndices.second;
        const pN = group.nonAxleIndices[0];
        const ab = Vec3.len(netGroup[p1], netGroup[pN]);
        const bc = Vec3.len(netGroup[pN], netGroup[p2]);

        const ratio = ab / bc;
        const minRatio = 1 / maxRatio;
        if (ltc(ratio, minRatio, 2) || gtc(ratio, maxRatio, 2)) {
          const edge1 = getSideName(p1, pN);
          const edge2 = getSideName(pN, p2);
          const error = CalcProductLogic.addError(errors, ProjectCheckErrorType.Warning, "net", lc.t("Rule_MaxSide_Ratio"), { params: { edge1, edge2, reason: reason ? `(${reason})` : "", maxRatio, ratio: Math.max(ratio, (1 / ratio)).toFixed(2) } })
          if (inputModel)
            error.addFields(inputModel, "sunSail", edge1 as any, edge2 as any)
        }
      }
    }
    return undefined;
  }

  static addSnapCounterPlateArticles(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId): void {
    const article = SunSailLogic.getRopeFixArticle(sunSail, poleId);
    if (article && (SunSailLogic.isSnapWinch(article) || SunSailLogic.isFallStopp(article))) {
      if (PoleLogic.isDroppole(SunSailLogic.getPoleMaterial(sunSail, poleId))) {
        // https://plaspack.atlassian.net/jira/software/projects/APP/issues/APP-1278
        CalcProductLogic.addRuleArticle(slc, "00E13000809", { affiliation: { poleId }, count: 1, offerList: "KH" }); // / Gegenplatte zu Snap-Grundplatte bei Droppole-Mast (bei X/XS inkludiert?)
      }
    }
  }

  static checkMaxMountSideLength(slc: ICalcContext, ssd: ISunSailData): void {
    const maxSideLength = SunSailLogic.getMaxMountSideLength(ssd.sunSailType);

    if (maxSideLength < Number.MAX_VALUE) {
      const topology = SunSailTopology.calcTopology(ssd.sunSailType, Poles.getCount(ssd));
      const sides = topology.sides.filter((s) => s.netBorder === true);
      const mountPositions = SunSailLogic.calcMountPositions(ssd, 0, undefined);
      for (const side of sides) {
        if (side.netBorder && !side.axle) {
          // for (let i = 0; i < side.groupIndices.length; i++) {
          const sideLength = Vec3.len(mountPositions[side.first], mountPositions[side.second]);
          if (gtc(sideLength, maxSideLength, 3)) {
            const sideName = getSideName(side.first, side.second);
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Error, sideName, slc.lc.t("Rule_MaxMountSideLength"), { params: [sideName.toLocaleUpperCase(), Measure.toMM(maxSideLength), Measure.toMM(sideLength)] });
          }
          // }
        }
      }
    }
  }

  static addCornerArticles(slc: ICalcContext, sunSail: ISunSailData): void {
    const clothCount = sunSail.clothCount || 1;
    const sunSailType = sunSail.sunSailType;
    const poleCount = Poles.getCount(sunSail);
    if (SST.is(sunSailType, SST.SF, SST.One)) {
      for (let i = 0; i < poleCount; i++) {
        const cornerType = SunSailLogic.getCornerType(sunSail, i);
        if (ArticleLogic.isCornerForAll(sunSailType, cornerType)) {
          for (let j = 0; j < poleCount; j++) {
            if (SunSailLogic.getCornerType(sunSail, j) !== cornerType)
              CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Hint, getPointName(i), slc.lc.t("Rule_Corner_Same"), { params: getCornerTypeName(cornerType, slc.lc) });
          }
        }

        if (cornerType && cornerType !== "None") {
          const articleId = SunSailLogic.getCornerArticleId(sunSail, i);
          if (!articleId)
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Error, getPointName(i), slc.lc.t("Rule_Article_ForCorner_NotFound"))
          else
            CalcProductLogic.addRuleArticle(slc, articleId, { affiliation: { poleId: i }, count: clothCount, idCategory: "corner", offerList: getOfferListForCornerArticle(articleId), partList: { list: "none" } }); // https://plaspack.atlassian.net/jira/software/projects/APP22/boards/2?selectedIssue=APP22-956
        }
      }
    }
  }

  static addEdgeItemArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    const clothCount = sunSail.clothCount || 1;
    const edgeNames = SunSailEdges.getEdgeNames(sunSail.sunSailType, Poles.getCount(sunSail));
    for (const edgeName of edgeNames) {
      const real = SunSailLogic.calcEdgeMountItemReal(sunSail, edgeName);
      if (real) {
        const articleIds: Record<EdgeMountItemType, string> = {
          "dringOld": "????",
          "grommet": "00E13001646",
          "strap": "00E13001943",
          "strapDesign": "00E13001944",
        }
        const articleId = articleIds[real.itemType];
        CalcProductLogic.addRuleArticle(slc, articleId, { count: real.count * clothCount, offerList: "S", partList: { list: "none" } });
      }
    }
  }


  static checkAlignDistOut(slc: ICalcContext, ssd: ISunSailData): void {
    if (SunSailLogic.alignDistOutRequiredForGuarantee(ssd.sunSailType, Poles.getCount(ssd))) {
      const alignDistOut = SunSailLogic.calcAlignDistOut(ssd);
      if (!alignDistOut)
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "net", slc.lc.t("Rule_Align_DistOut"));
    }
  }

  static checkOperationDefelectionPoint(slc: ICalcContext, sunSail: ISunSailData): void {
    if (sunSail.sunSailType === SunSailType.SolidayOne && Poles.getCount(sunSail) === 4) {
      const deflectionPosition = SunSailLogic.getOperatingDeflectionPosition(sunSail);
      if (deflectionPosition) {
        {
          const distOutDP = SunSailLogic.calcDistanceOut(sunSail, deflectionPosition)
          const distOutOther = SunSailLogic.calcDistanceOut(sunSail, Poles.getOtherPole(sunSail.sunSailType, deflectionPosition))
          if (ltc(distOutDP, distOutOther, 2)) {
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Error, "net", slc.lc.t("Rule_One_DistOut"))
          }
        }
        {
          const l = SunSailLogic._calcOnePulleySystemDistance(sunSail);
          const thresholdHeight = 1.1;
          if (ltc(l, thresholdHeight, 3)) {
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(SunSailLogic.getCoilUpPoleIndex()), slc.lc.t("Rule_One_PulleySystemHeight"), { params: [getPointName(SunSailLogic.getCoilUpPoleIndex()), Measure.toMM(thresholdHeight), Measure.toMM(l)] })
          }
        }
      }
    }
  }

  static addArticles(slc: ICalcContext, ssd: ISunSailData): void {
    const withHW = Product.getOrderType(ssd).includes("H");
    // console.log("addArticles", ssd.orderType);
    CalcSailLogic.addSailClothArticles(slc, ssd);
    CalcSailLogic.addHemstitchArticles(slc, ssd);
    CalcSailLogic.addKederArticles(slc, ssd);

    if (withHW) {
      CalcSail3dLogic.addSystemArticles(slc, ssd);
    }
    CalcSail3dLogic.addSystemSailArticles(slc, ssd);

    if (withHW) {
      CalcSail3dLogic.addAxleArticles(slc, ssd);
      CalcSail3dLogic.addMotorArticles(slc, ssd);
      CalcSail3dLogic.addAxleDrillTemplateArticles(slc, ssd);
      CalcSail3dLogic.addIndirectPylonArticles(slc, ssd);
      CalcSail3dLogic.addTwistingSetArticles(slc, ssd);

      CalcSail3dLogic.addAllPointArticles(slc, ssd);
    }
    CalcSailLogic.addCornerArticles(slc, ssd);
    CalcSailLogic.addEdgeItemArticles(slc, ssd);

    if (withHW) {
      CalcSail3dLogic.addCleanerArticles(slc, ssd);
      CalcSail3dLogic.addFrachtForPoles(slc, ssd);
      CalcSail3dLogic.addStatikArticle(slc, ssd);
    }
    CalcProductLogic.addFlyer(slc);
  }

  static checkMaxNetSideLength(slc: ICalcContext, ssd: ISunSailData): void {
    const pylonCount = SunSailLogic.calcPylonCount(ssd);
    const maxSideLength = SunSailLogic.getMaxNetSideLength(ssd.sunSailType, FL.fromProduct(ssd), pylonCount);

    if (maxSideLength.warning < Number.MAX_VALUE || maxSideLength.error < Number.MAX_VALUE) {
      const topology = SunSailTopology.calcTopology(ssd.sunSailType, Poles.getCount(ssd));
      const sides = topology.sides.filter((s) => s.netBorder === true);
      const netGroups = SunSailLogic.calcNetPositionGroups(ssd, {});
      for (const side of sides) {
        if (side.netBorder && !side.axle) {
          for (const g of side.groupIndices) {
            const netGroup = netGroups[g];
            const sideLength = Vec3.len(netGroup[side.first], netGroup[side.second]);
            if (gtc(sideLength, maxSideLength.error, 3)) {
              const sideName = getSideNameSmall(side.first, side.second);
              CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Error, sideName, slc.lc.t("Rule_MaxNetSideLength"), { params: [sideName.toLocaleUpperCase(), Measure.toMM(maxSideLength.error), Measure.toMM(sideLength)] });
            } else if (gtc(sideLength, maxSideLength.warning, 3)) {
              const sideName = getSideNameSmall(side.first, side.second);
              CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, sideName, slc.lc.t("Rule_MaxNetSideLength"), { params: [sideName.toLocaleUpperCase(), Measure.toMM(maxSideLength.warning), Measure.toMM(sideLength)] });
            }
          }
        }
      }
    }
  }

  static checkMinAngles(slc: ICalcContext, ssd: ISunSailData): void {
    if (SST.is(ssd.sunSailType, SST.F)) {
      const minAngleDeg = SunSailLogic.getMinAngleDeg(ssd.sunSailType, FL.fromProduct(ssd));

      const netGroups = SunSailLogic.calcNetPositionGroups(ssd, {});
      const topology = SunSailTopology.calcTopology(ssd.sunSailType, Poles.getCount(ssd));

      for (let ig = 0; ig < topology.groups.length; ig++) {
        const poleIndices = topology.groups[ig].poleIndices;
        for (let i = 0; i < poleIndices.length; i++) {
          const angle = SunSailLogic.calcAngleForIndex(i, poleIndices, netGroups[ig])
          const degAngle = MathUtil.rad2deg(angle);
          if (!isNaN(angle) && gtc(minAngleDeg, degAngle, 1)) {
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Error, getPointName(poleIndices[i]).toLowerCase() as any, slc.lc.t("Rule_MinAngle"), { params: [minAngleDeg, degAngle.toFixed(1)] });
          }
        }
      }
    }
  }
}